import { useEffect, useState } from 'react';
import { Eye, CalendarDays, Mail } from 'lucide-react';
import { getBusinessDiagnosticLeads, getMarketingDiagnosticLeads } from '../api/diagnosticsApi';
import { DiagnosticLeadCaptureDetail } from '../types/diagnostics';
import DiagnosticLeadModal from './DiagnosticLeadModal';

interface Props {
  type: 'marketing' | 'business';
}

export default function DiagnosticLeadsTable({ type }: Props) {
  const [leads, setLeads] = useState<DiagnosticLeadCaptureDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  useEffect(() => {
    const loadLeads = async () => {
      setLoading(true);
      try {
        const data = type === 'marketing'
          ? await getMarketingDiagnosticLeads()
          : await getBusinessDiagnosticLeads();
        setLeads(data);
      } finally {
        setLoading(false);
      }
    };

    loadLeads();
  }, [type]);

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{type === 'marketing' ? 'Marketing Diagnostic Leads' : 'Business Assessment Leads'}</h2>
          <p className="text-sm text-slate-500">Review submitted diagnostic captures and follow-up status.</p>
        </div>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-slate-500">Loading leads...</p>
      ) : leads.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500">No leads have been captured yet.</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Company</th>
                <th className="py-3 pr-4">Email</th>
                <th className="py-3 pr-4">Date</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((lead) => (
                <tr key={lead.id} className="text-sm text-slate-700">
                  <td className="py-3 pr-4 font-semibold">{lead.name || 'Anonymous'}</td>
                  <td className="py-3 pr-4">{lead.company || '—'}</td>
                  <td className="py-3 pr-4">{lead.email || '—'}</td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <CalendarDays size={14} className="text-slate-400" />
                      {new Date(lead.createdDateUtc).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className={lead.isEmailSent ? 'text-emerald-600' : 'text-slate-400'} />
                      {lead.isEmailSent ? 'Email sent' : 'Pending'}
                    </div>
                  </td>
                  <td className="py-3">
                    <button
                      onClick={() => setSelectedLeadId(lead.id)}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DiagnosticLeadModal
        isOpen={Boolean(selectedLeadId)}
        leadId={selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
      />
    </div>
  );
}
